import React, { useState, useEffect } from "react";
import {
  Box, Typography, Button, Grid, Card, CardContent, Divider,
  FormControl, InputLabel, Select, MenuItem, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Checkbox, FormControlLabel, Alert, Paper, Chip, Autocomplete,
  Collapse, IconButton, ListSubheader,
  Dialog, DialogTitle, DialogContent, DialogActions
} from "@mui/material";
import { CheckCircle, ArrowBack, ExpandMore, ExpandLess } from "@mui/icons-material";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "";

export default function PasoConfirmarCompra({
  factura,
  retenciones,
  cuentas,
  productos,
  onConfirmar,
  onCancelar,
  loading
}) {
  // Estados del formulario
  const [cuentasSeleccionadas, setCuentasSeleccionadas] = useState({
    ivaPagado: "",
    retFuente: "",
    retIVA: "",
    cuentaPago: "",
    cuentaDestino: "" // Tipo de gasto/destino (editable)
  });

  const [detallesInventario, setDetallesInventario] = useState([]);
  
  // Estado para clasificación de ítems (GASTO vs INVENTARIO)
  const [clasificacionItems, setClasificacionItems] = useState([]);
  
  // Estado para selección de productos existentes
  const [productosSeleccionados, setProductosSeleccionados] = useState([]);
  
  // Estado para datos de productos nuevos
  const [productosNuevos, setProductosNuevos] = useState([]);
  
  // Estado para configuración contable
  const [configuracionContable, setConfiguracionContable] = useState(null);

  // Bodegas dinámicas
  const [bodegasLista, setBodegasLista] = useState([]);
  
  const [condicionesPago, setCondicionesPago] = useState({
    forma_pago: "CONTADO",
    dias_credito: 0,
    fecha_vencimiento: "",
    fecha_ingreso: new Date().toISOString().split("T")[0], // Fecha de ingreso modificable
    valor_a_pagar: 0,
    observaciones_pago: ""
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  };

  // Cargar configuración contable
  const cargarConfiguracionContable = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/configuracion/contable`, {
        headers: getAuthHeaders()
      });
      setConfiguracionContable(res.data);
      console.log("📊 Configuración contable cargada:", res.data);
    } catch (error) {
      console.error("Error al cargar configuración contable:", error);
      // Usar configuración por defecto si no existe
      setConfiguracionContable({
        cuenta_inventario_general: "",
        cuenta_gastos_general: "",
        cuenta_iva_compras: "",
        cuenta_proveedores_general: "",
        cuenta_caja_general: "",
        cuenta_bancos_general: ""
      });
    }
  };

  // Pre-seleccionar cuentas comunes (solo las principales, no generales)
  useEffect(() => {
    cargarConfiguracionContable();
    // Cargar bodegas
    axios.get(`${API_URL}/api/bodegas`, { headers: getAuthHeaders() })
      .then(res => setBodegasLista(res.data?.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (cuentas.length > 0 && configuracionContable) {
      const mapeo = {
        ivaPagado: configuracionContable.cuenta_iva_compras || cuentas.find(c => c.codigo === "1010401")?.id || "",
        retFuente: configuracionContable.cuenta_retencion_fuente || cuentas.find(c => c.codigo === "2010502")?.id || "",
        retIVA: configuracionContable.cuenta_retencion_iva || cuentas.find(c => c.codigo === "2010503")?.id || "",
        cuentaPago: condicionesPago.forma_pago === "CONTADO" 
          ? (configuracionContable.cuenta_caja_general || cuentas.find(c => c.codigo === "1010101")?.id || "")
          : (configuracionContable.cuenta_proveedores_general || cuentas.find(c => c.codigo === "2010101")?.id || ""),
        cuentaDestino: configuracionContable.cuenta_inventario_general || cuentas.find(c => c.codigo === "1010301")?.id || ""
      };
      setCuentasSeleccionadas(mapeo);
    }
  }, [cuentas, condicionesPago.forma_pago, configuracionContable]);

  // Debug: Mostrar estructura de la factura
  useEffect(() => {
    if (factura) {
      console.log("📄 Estructura completa de la factura:", factura);
      console.log("📦 Detalles encontrados en:", {
        detalles: factura.detalles,
        productos: factura.productos,
        items: factura.items
      });
    }
  }, [factura]);

  // Inicializar detalles de inventario
  useEffect(() => {
    if (factura) {
      // Buscar detalles en diferentes ubicaciones posibles
      const items = factura.detalles || factura.productos || [];
      console.log("📦 Inicializando detalles de inventario:", items);
      
      const detalles = items.map((detalle, index) => ({
        detalle_id: index,
        afectaInventario: true, // Por defecto todos afectan
        esNuevo: true, // Por defecto crear como nuevo
        actualizarPrecio: true,
        cuentaContable: "", // Cuenta contable específica para este ítem
        datos: detalle
      }));
      setDetallesInventario(detalles);

      // Inicializar clasificación de ítems
      const clasificaciones = items.map((detalle, index) => ({
        item_id: index,
        tipo: "", // Sin clasificar — el usuario decide
        producto_existente_id: null,
        crear_nuevo: true,
        deducible: false,
        inventario: false, // No marcar por defecto
        bodega: "principal",
        datos: detalle
      }));
      setClasificacionItems(clasificaciones);

      // Inicializar selección de productos
      const selecciones = items.map((detalle, index) => ({
        item_id: index,
        producto_seleccionado: null,
        crear_nuevo: true,
        datos: detalle
      }));
      setProductosSeleccionados(selecciones);

      // Inicializar datos de productos nuevos (solo campos esenciales)
      const productosNuevosInit = items.map((detalle, index) => ({
        item_id: index,
        codigo: detalle.codigoPrincipal || "",
        nombre: detalle.descripcion || "",
        precio_compra: parseFloat(detalle.precioUnitario) || 0,
        precio_venta: (parseFloat(detalle.precioUnitario) || 0) * (1 + (configuracionContable?.porcentaje_utilidad || 30) / 100),
        unidad_medida: "UND",
        categoria: ""
      }));
      setProductosNuevos(productosNuevosInit);
    }
  }, [factura]);

  // Actualizar cuenta de pago cuando cambie la forma de pago
  useEffect(() => {
    if (cuentas.length > 0 && configuracionContable) {
      const nuevaCuentaPago = condicionesPago.forma_pago === "CONTADO" 
        ? (configuracionContable.cuenta_caja_general || cuentas.find(c => c.codigo === "1010101")?.id || "")
        : (configuracionContable.cuenta_proveedores_general || cuentas.find(c => c.codigo === "2010101")?.id || "");
      
      setCuentasSeleccionadas(prev => ({
        ...prev,
        cuentaPago: nuevaCuentaPago
      }));
    }
  }, [condicionesPago.forma_pago, cuentas, configuracionContable]);

  // Inicializar valor a pagar
  useEffect(() => {
    if (factura && factura.totales) {
      setCondicionesPago(prev => ({
        ...prev,
        valor_a_pagar: parseFloat(factura.totales.importeTotal || 0)
      }));
    }
  }, [factura]);

  const handleSubmit = () => {
    // Validar cuentas principales
    if (!cuentasSeleccionadas.ivaPagado || !cuentasSeleccionadas.cuentaPago || !cuentasSeleccionadas.cuentaDestino) {
      alert("Debe seleccionar el tipo de gasto/destino de la compra");
      return;
    }

    // Preparar datos con clasificación de ítems usando configuración
    const datosCompra = {
      cuentasContables: {
        ...cuentasSeleccionadas,
        // Agregar cuentas de la configuración
        inventario: configuracionContable?.cuenta_inventario_general || "",
        gastos: configuracionContable?.cuenta_gastos_general || ""
      },
      // Usar clasificación de ítems en lugar de detallesInventario
      detallesInventario: clasificacionItems.map((item, index) => ({
        afecta_inventario: item.tipo === "INVENTARIO",
        es_gasto: item.tipo === "GASTO",
        es_deducible: item.deducible || false,
        es_inventario: item.inventario || false,
        es_nuevo: !item.producto_existente_id && item.tipo === "INVENTARIO",
        actualizar_precio: item.tipo === "INVENTARIO" && !!item.producto_existente_id,
        tipo_clasificacion: item.tipo,
        producto_existente_id: item.producto_existente_id || null,
        bodega: item.bodega || "principal",
        // Asignar cuenta automáticamente según configuración
        cuenta_contable: item.tipo === "INVENTARIO" 
          ? configuracionContable?.cuenta_inventario_general 
          : configuracionContable?.cuenta_gastos_general,
        datos_producto: {
          codigo: item.datos.codigoPrincipal || `AUTO-${Date.now()}-${index}`,
          nombre: item.datos.descripcion,
          cantidad: parseFloat(item.datos.cantidad) || 0,
          precio_compra: parseFloat(item.datos.precioUnitario) || 0,
          precio_venta: parseFloat(productosNuevos[index]?.precio_venta) || (parseFloat(item.datos.precioUnitario) || 0) * (1 + (configuracionContable?.porcentaje_utilidad || 30) / 100),
          unidad_medida: "UND",
          categoria: "",
          tipo: item.tipo === "INVENTARIO" ? "PRODUCTO" : "SERVICIO",
          bodega: item.bodega || "principal"
        }
      })),
      condicionesPago: {
        ...condicionesPago,
        valor_a_pagar: parseFloat(condicionesPago.valor_a_pagar),
        // Agregar información de la cuenta de pago seleccionada
        cuenta_pago_id: cuentasSeleccionadas.cuentaPago,
        es_contado: condicionesPago.forma_pago === "CONTADO"
      },
      // Incluir clasificación para el backend
      clasificacionItems: clasificacionItems,
      // Incluir configuración contable usada
      configuracionContable: configuracionContable
    };

    onConfirmar(datosCompra);
  };

  const toggleAfectaInventario = (index) => {
    const nuevos = [...detallesInventario];
    nuevos[index].afectaInventario = !nuevos[index].afectaInventario;
    setDetallesInventario(nuevos);
  };

  const toggleActualizarPrecio = (index) => {
    const nuevos = [...detallesInventario];
    nuevos[index].actualizarPrecio = !nuevos[index].actualizarPrecio;
    setDetallesInventario(nuevos);
  };

  const updateCuentaContable = (index, cuentaId) => {
    const nuevos = [...detallesInventario];
    nuevos[index].cuentaContable = cuentaId;
    setDetallesInventario(nuevos);
  };

  // Funciones para clasificación de ítems
  const cambiarClasificacionItem = (index, tipo) => {
    const nuevas = [...clasificacionItems];
    nuevas[index].tipo = tipo;
    nuevas[index].inventario = tipo === "INVENTARIO";
    // No auto-seleccionar cuenta general, dejar vacío para forzar selección específica
    nuevas[index].cuenta_contable = "";
    setClasificacionItems(nuevas);
  };

  const updateCuentaClasificacion = (index, cuentaId) => {
    const nuevas = [...clasificacionItems];
    nuevas[index].cuenta_contable = cuentaId;
    
    // Auto-determinar tipo según la cuenta seleccionada
    const cuentaSel = cuentas.find(c => c.id === cuentaId);
    if (cuentaSel) {
      if (cuentaSel.codigo?.startsWith("10105") || cuentaSel.codigo?.startsWith("10103") || cuentaSel.tipo === "ACTIVO" && cuentaSel.nombre?.toLowerCase().includes("inventario")) {
        nuevas[index].tipo = "INVENTARIO";
        nuevas[index].inventario = true;
      } else if (cuentaSel.codigo?.startsWith("5") || cuentaSel.tipo === "GASTO") {
        nuevas[index].tipo = "GASTO";
        nuevas[index].inventario = false;
      }
    }
    
    setClasificacionItems(nuevas);
  };

  // Funciones para selección de productos
  const seleccionarProductoExistente = (index, productoId) => {
    const nuevas = [...clasificacionItems];
    nuevas[index].producto_existente_id = productoId;
    nuevas[index].crear_nuevo = !productoId; // Si selecciona producto, no crear nuevo
    setClasificacionItems(nuevas);
  };

  const toggleCrearNuevo = (index, crear) => {
    const nuevas = [...clasificacionItems];
    nuevas[index].crear_nuevo = crear;
    if (crear) {
      nuevas[index].producto_existente_id = null; // Si crea nuevo, limpiar selección
    }
    setClasificacionItems(nuevas);
  };

  // Toggle deducible
  const toggleDeducible = (index) => {
    const nuevas = [...clasificacionItems];
    nuevas[index].deducible = !nuevas[index].deducible;
    setClasificacionItems(nuevas);
  };

  // Toggle inventario
  const toggleInventarioItem = (index) => {
    const nuevas = [...clasificacionItems];
    nuevas[index].inventario = !nuevas[index].inventario;
    // Sync tipo con el checkbox
    nuevas[index].tipo = nuevas[index].inventario ? "INVENTARIO" : "GASTO";
    // Si se desmarca inventario, limpiar producto vinculado
    if (!nuevas[index].inventario) {
      nuevas[index].producto_existente_id = null;
    }
    setClasificacionItems(nuevas);
  };

  // Editar datos del ítem (código, descripción, cantidad, precio)
  const updateItemDatos = (index, campo, valor) => {
    const nuevas = [...clasificacionItems];
    nuevas[index].datos = { ...nuevas[index].datos, [campo]: valor };
    // Recalcular subtotal si cambia cantidad o precio
    if (campo === 'cantidad' || campo === 'precioUnitario') {
      const cant = parseFloat(campo === 'cantidad' ? valor : nuevas[index].datos.cantidad) || 0;
      const precio = parseFloat(campo === 'precioUnitario' ? valor : nuevas[index].datos.precioUnitario) || 0;
      nuevas[index].datos.precioTotalSinImpuesto = (cant * precio).toFixed(2);
    }
    setClasificacionItems(nuevas);
  };

  // Diálogo para vincular producto
  const [dialogoProductoAbierto, setDialogoProductoAbierto] = useState(false);
  const [itemIndexProducto, setItemIndexProducto] = useState(null);
  const [busquedaProducto, setBusquedaProducto] = useState('');

  const abrirDialogoProducto = (index) => {
    setItemIndexProducto(index);
    setBusquedaProducto(clasificacionItems[index]?.datos?.descripcion || '');
    setDialogoProductoAbierto(true);
  };

  const handleVincularProducto = (producto) => {
    if (itemIndexProducto === null) return;
    const nuevas = [...clasificacionItems];
    nuevas[itemIndexProducto].producto_existente_id = producto.id;
    nuevas[itemIndexProducto].datos = {
      ...nuevas[itemIndexProducto].datos,
      codigoPrincipal: producto.codigo || nuevas[itemIndexProducto].datos.codigoPrincipal,
    };
    setClasificacionItems(nuevas);
    setDialogoProductoAbierto(false);
  };

  // Editar bodega
  const updateBodega = (index, bodega) => {
    const nuevas = [...clasificacionItems];
    nuevas[index].bodega = bodega;
    setClasificacionItems(nuevas);
  };

  // Funciones para manejar datos de productos nuevos
  const updateProductoNuevo = (index, campo, valor) => {
    const nuevos = [...productosNuevos];
    nuevos[index][campo] = valor;
    
    // Auto-calcular precio de venta si cambia precio de compra
    if (campo === 'precio_compra') {
      nuevos[index].precio_venta = parseFloat(valor) * (1 + (configuracionContable?.porcentaje_utilidad || 30) / 100);
    }
    
    setProductosNuevos(nuevos);
  };

  // Función para organizar cuentas jerárquicamente
  const organizarCuentasJerarquicas = (cuentasFiltradas) => {
    const grupos = {};
    
    cuentasFiltradas.forEach(cuenta => {
      if (!cuenta.codigo) return;
      
      // Extraer el primer nivel (ej: "1" de "1010301")
      const nivel1 = cuenta.codigo.substring(0, 1);
      // Extraer niveles — si tiene puntos usar split, si no usar substring
      const codigoStr = cuenta.codigo;
      const nivel2 = codigoStr.includes('.') 
        ? codigoStr.split('.').slice(0, 2).join('.')
        : codigoStr.substring(0, 3); // ej: "101" de "1010301"
      
      if (!grupos[nivel1]) {
        grupos[nivel1] = {
          nombre: getNombreNivel1(nivel1),
          subgrupos: {}
        };
      }
      
      if (!grupos[nivel1].subgrupos[nivel2]) {
        grupos[nivel1].subgrupos[nivel2] = {
          nombre: nivel2,
          cuentas: []
        };
      }
      
      grupos[nivel1].subgrupos[nivel2].cuentas.push(cuenta);
    });
    
    return grupos;
  };

  // Nombres para los niveles principales
  const getNombreNivel1 = (codigo) => {
    const nombres = {
      '1': '1. ACTIVOS',
      '2': '2. PASIVOS', 
      '3': '3. PATRIMONIO',
      '4': '4. INGRESOS',
      '5': '5. GASTOS',
      '6': '6. COSTOS'
    };
    return nombres[codigo] || `${codigo}. OTROS`;
  };

  // Nombres para los subgrupos
  const getNombreNivel2 = (codigo) => {
    const nombres = {
      '1.01': '1.01 Activo Corriente',
      '1.02': '1.02 Activo No Corriente',
      '2.01': '2.01 Pasivo Corriente',
      '2.02': '2.02 Pasivo No Corriente',
      '3.01': '3.01 Capital',
      '4.01': '4.01 Ingresos Operacionales',
      '5.01': '5.01 Gastos Operacionales',
      '5.02': '5.02 Gastos No Operacionales'
    };
    return nombres[codigo] || codigo;
  };

  // Renderizar opciones jerárquicas para Select
  const renderOpcionesJerarquicas = (cuentasFiltradas) => {
    const grupos = organizarCuentasJerarquicas(cuentasFiltradas);
    const opciones = [];
    
    Object.entries(grupos).forEach(([nivel1, grupo]) => {
      // Agregar header del grupo principal
      opciones.push(
        <ListSubheader key={nivel1} sx={{ bgcolor: '#f5f5f5', fontWeight: 600 }}>
          {grupo.nombre}
        </ListSubheader>
      );
      
      Object.entries(grupo.subgrupos).forEach(([nivel2, subgrupo]) => {
        // Agregar header del subgrupo
        if (Object.keys(grupo.subgrupos).length > 1) {
          opciones.push(
            <ListSubheader key={nivel2} sx={{ bgcolor: '#fafafa', pl: 3, fontSize: '0.9em' }}>
              {subgrupo.nombre}
            </ListSubheader>
          );
        }
        
        // Agregar las cuentas del subgrupo
        subgrupo.cuentas.forEach(cuenta => {
          opciones.push(
            <MenuItem key={cuenta.id} value={cuenta.id} sx={{ pl: 4 }}>
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  {cuenta.codigo}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {cuenta.nombre}
                </Typography>
              </Box>
            </MenuItem>
          );
        });
      });
    });
    
    return opciones;
  };

  const calcularFechaVencimiento = (dias, fechaBase = null) => {
    if (dias <= 0) return "";
    const fecha = new Date(fechaBase || condicionesPago.fecha_ingreso || factura.factura.fechaEmision);
    fecha.setDate(fecha.getDate() + dias);
    return fecha.toISOString().split("T")[0];
  };

  if (!factura) return null;

  return (
    <Box sx={{ py: 2 }}>
      <Typography variant="h6" gutterBottom>
        ✅ Paso 2: Confirmar Compra
      </Typography>

      {/* Datos del Proveedor */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom color="primary">
            👤 Proveedor
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">RUC:</Typography>
              <Typography variant="body1" fontWeight={600}>{factura.proveedor.ruc}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">Razón Social:</Typography>
              <Typography variant="body1" fontWeight={600}>{factura.proveedor.razonSocial}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">Dirección:</Typography>
              <Typography variant="body1">{factura.proveedor.direccion}</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Clasificación de Productos e Ítems */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom color="primary">
            📦 Items de la Compra
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Edite los campos si es necesario. Solo los ítems marcados como <strong>Inventario</strong> pueden vincularse con productos existentes. Las cuentas contables se asignan automáticamente según la configuración: <strong>Inventario</strong> → Cuenta de Inventario, <strong>Gasto</strong> → Cuenta de Gastos.
          </Alert>

          {/* Mostrar configuración de cuentas */}
          
          {clasificacionItems.length === 0 ? (
            <Alert severity="warning">
              No se encontraron productos en el XML. Verifique que el archivo XML contenga detalles de productos.
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 900 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: "#1a237e", '& th': { color: '#fff', fontWeight: 'bold', py: 1.5, px: 1.5, fontSize: '0.8rem' } }}>
                    <TableCell sx={{ width: 100 }}>Código</TableCell>
                    <TableCell sx={{ minWidth: 250 }}>Descripción</TableCell>
                    <TableCell sx={{ width: 70 }} align="center">Cant.</TableCell>
                    <TableCell sx={{ width: 90 }} align="right">P. Unit.</TableCell>
                    <TableCell sx={{ width: 90 }} align="right">PVP</TableCell>
                    <TableCell sx={{ width: 90 }} align="right">Subtotal</TableCell>
                    <TableCell sx={{ width: 120 }} align="center">Producto</TableCell>
                    <TableCell sx={{ width: 70 }} align="center">Deduc.</TableCell>
                    <TableCell sx={{ width: 70 }} align="center">Invent.</TableCell>
                    <TableCell sx={{ width: 140 }} align="center">Bodega</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {clasificacionItems.map((item, index) => (
                    <TableRow key={index} sx={{
                      bgcolor: item.inventario ? '#e3f2fd' : item.deducible ? '#e8f5e9' : 'inherit',
                      '& td': { py: 1, px: 1.5 }
                    }}>
                      <TableCell>
                        <TextField
                          size="small"
                          value={item.datos.codigoPrincipal || ''}
                          variant="outlined"
                          onChange={(e) => updateItemDatos(index, 'codigoPrincipal', e.target.value)}
                          inputProps={{ style: { fontSize: '0.8rem' } }}
                          sx={{ '& .MuiOutlinedInput-root': { height: 32 } }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          value={item.datos.descripcion || ''}
                          variant="outlined"
                          onChange={(e) => updateItemDatos(index, 'descripcion', e.target.value)}
                          inputProps={{ style: { fontSize: '0.8rem' } }}
                          sx={{ '& .MuiOutlinedInput-root': { height: 32 } }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={item.datos.cantidad || ''}
                          variant="outlined"
                          onChange={(e) => updateItemDatos(index, 'cantidad', e.target.value)}
                          inputProps={{ style: { fontSize: '0.8rem', textAlign: 'center' } }}
                          sx={{ '& .MuiOutlinedInput-root': { height: 32, width: 60 } }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={item.datos.precioUnitario || ''}
                          variant="outlined"
                          onChange={(e) => updateItemDatos(index, 'precioUnitario', e.target.value)}
                          inputProps={{ style: { fontSize: '0.8rem' } }}
                          sx={{ '& .MuiOutlinedInput-root': { height: 32, width: 80 } }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={productosNuevos[index]?.precio_venta || ''}
                          variant="outlined"
                          onChange={(e) => updateProductoNuevo(index, 'precio_venta', e.target.value)}
                          inputProps={{ style: { fontSize: '0.8rem' } }}
                          sx={{ '& .MuiOutlinedInput-root': { height: 32, width: 80 } }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold" fontSize="0.85rem" color="primary">
                          ${item.datos.precioTotalSinImpuesto || '0.00'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {item.inventario ? (
                          item.producto_existente_id ? (
                            <Chip
                              label={productos.find(p => p.id === item.producto_existente_id)?.nombre?.substring(0, 12) || '✓'}
                              size="small"
                              color="success"
                              onDelete={() => seleccionarProductoExistente(index, null)}
                              sx={{ fontSize: '0.7rem', height: 24, maxWidth: 110 }}
                            />
                          ) : (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => abrirDialogoProducto(index)}
                              sx={{ fontSize: '0.7rem', minWidth: 0, px: 1, py: 0.5, height: 28 }}
                            >
                              🔗 Vincular
                            </Button>
                          )
                        ) : (
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            Solo inventario
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          size="small"
                          checked={!!item.deducible}
                          onChange={() => toggleDeducible(index)}
                          color="success"
                          sx={{ p: 0.5 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          size="small"
                          checked={!!item.inventario}
                          onChange={() => toggleInventarioItem(index)}
                          color="primary"
                          sx={{ p: 0.5 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <FormControl size="small" fullWidth>
                          <Select
                            value={item.bodega || 'principal'}
                            onChange={(e) => updateBodega(index, e.target.value)}
                            disabled={!item.inventario}
                            sx={{ 
                              fontSize: '0.8rem', 
                              height: 32,
                              opacity: item.inventario ? 1 : 0.5
                            }}
                          >
                            <MenuItem value="principal" sx={{ fontSize: '0.8rem' }}>
                              📦 Principal
                            </MenuItem>
                            {bodegasLista.length > 0 ? (
                              bodegasLista.filter(b => !b.es_principal).map(b => (
                                <MenuItem key={b.id} value={b.id} sx={{ fontSize: '0.8rem' }}>
                                  🏭 {b.nombre}
                                </MenuItem>
                              ))
                            ) : (
                              <>
                                <MenuItem value="secundaria" sx={{ fontSize: '0.8rem' }}>
                                  🏪 Secundaria
                                </MenuItem>
                                <MenuItem value="almacen" sx={{ fontSize: '0.8rem' }}>
                                  🏭 Almacén
                                </MenuItem>
                              </>
                            )}
                          </Select>
                        </FormControl>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Diálogo Vincular Producto */}
          <Dialog open={dialogoProductoAbierto} onClose={() => setDialogoProductoAbierto(false)} maxWidth="sm" fullWidth>
            <DialogTitle>🔗 Vincular Producto Existente</DialogTitle>
            <DialogContent>
              <TextField
                fullWidth
                label="Buscar producto"
                value={busquedaProducto}
                onChange={(e) => setBusquedaProducto(e.target.value)}
                sx={{ mt: 1, mb: 2 }}
                placeholder="Buscar por nombre o código..."
              />
              {productos.length > 0 ? (
                <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Código</TableCell>
                        <TableCell>Nombre</TableCell>
                        <TableCell align="right">Precio</TableCell>
                        <TableCell>Acción</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {productos
                        .filter(p => 
                          !busquedaProducto || 
                          p.nombre?.toLowerCase().includes(busquedaProducto.toLowerCase()) ||
                          p.codigo?.toLowerCase().includes(busquedaProducto.toLowerCase())
                        )
                        .slice(0, 20)
                        .map((prod) => (
                          <TableRow key={prod.id} hover sx={{ cursor: 'pointer' }} onClick={() => handleVincularProducto(prod)}>
                            <TableCell>{prod.codigo}</TableCell>
                            <TableCell>{prod.nombre}</TableCell>
                            <TableCell align="right">${parseFloat(prod.precio_1 || 0).toFixed(2)}</TableCell>
                            <TableCell>
                              <Button size="small" variant="contained" onClick={() => handleVincularProducto(prod)}>
                                Seleccionar
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info">No hay productos registrados.</Alert>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDialogoProductoAbierto(false)}>Cancelar</Button>
            </DialogActions>
          </Dialog>

          {/* Resumen de Clasificación */}
          {clasificacionItems.length > 0 && (
            <Box sx={{ mt: 2, p: 2, bgcolor: "#f3e5f5", borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                📊 Resumen:
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Typography variant="body2" color="primary" fontWeight={600}>
                    📦 INVENTARIO ({clasificacionItems.filter(i => i.inventario).length} ítems):
                  </Typography>
                  {clasificacionItems
                    .filter(i => i.inventario)
                    .map((item, idx) => (
                      <Typography key={idx} variant="body2" sx={{ fontSize: 11, ml: 1 }}>
                        • {item.datos.descripcion?.substring(0, 30)}
                        {item.producto_existente_id ? ' (vinculado)' : ''} 
                        (${item.datos.precioTotalSinImpuesto})
                      </Typography>
                    ))}
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2" color="secondary" fontWeight={600}>
                    💰 GASTOS ({clasificacionItems.filter(i => !i.inventario).length} ítems):
                  </Typography>
                  {clasificacionItems
                    .filter(i => !i.inventario)
                    .map((item, idx) => (
                      <Typography key={idx} variant="body2" sx={{ fontSize: 11, ml: 1 }}>
                        • {item.datos.descripcion?.substring(0, 30)} (${item.datos.precioTotalSinImpuesto})
                      </Typography>
                    ))}
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2" color="success.main" fontWeight={600}>
                    ✅ DEDUCIBLE ({clasificacionItems.filter(i => i.deducible).length} ítems):
                  </Typography>
                  {clasificacionItems
                    .filter(i => i.deducible)
                    .map((item, idx) => (
                      <Typography key={idx} variant="body2" sx={{ fontSize: 11, ml: 1 }}>
                        • {item.datos.descripcion?.substring(0, 30)} (${item.datos.precioTotalSinImpuesto})
                      </Typography>
                    ))}
                </Grid>
              </Grid>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Datos de la Factura */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom color="primary">
            📄 Factura
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="text.secondary">Número:</Typography>
              <Typography variant="body1" fontWeight={600}>{factura.factura.numeroComprobante}</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="text.secondary">Fecha:</Typography>
              <Typography variant="body1" fontWeight={600}>{factura.factura.fechaEmision}</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="text.secondary">Total:</Typography>
              <Typography variant="h6" color="primary" fontWeight={700}>
                ${factura.totales.importeTotal}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Condiciones de Pago */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom color="primary">
            💰 Condiciones de Pago
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Fecha de Ingreso"
                type="date"
                value={condicionesPago.fecha_ingreso}
                onChange={(e) => {
                  const nuevaFecha = e.target.value;
                  setCondicionesPago({
                    ...condicionesPago,
                    fecha_ingreso: nuevaFecha,
                    fecha_vencimiento: calcularFechaVencimiento(condicionesPago.dias_credito, nuevaFecha)
                  });
                }}
                InputLabelProps={{ shrink: true }}
                helperText="Fecha de registro"
              />
            </Grid>
            
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Forma de Pago</InputLabel>
                <Select
                  value={condicionesPago.forma_pago}
                  label="Forma de Pago"
                  onChange={(e) => {
                    const forma = e.target.value;
                    const dias = forma === "CONTADO" ? 0 : condicionesPago.dias_credito;
                    setCondicionesPago({
                      ...condicionesPago,
                      forma_pago: forma,
                      dias_credito: dias,
                      fecha_vencimiento: calcularFechaVencimiento(dias)
                    });
                  }}
                >
                  <MenuItem value="CONTADO">Contado</MenuItem>
                  <MenuItem value="CREDITO">Crédito</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Valor a Pagar"
                type="number"
                value={condicionesPago.valor_a_pagar}
                InputProps={{ readOnly: true, startAdornment: "$" }}
                helperText="Total factura"
              />
            </Grid>

            {condicionesPago.forma_pago === "CREDITO" && (
              <>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Días de Crédito"
                    type="number"
                    value={condicionesPago.dias_credito}
                    onChange={(e) => {
                      const dias = parseInt(e.target.value) || 0;
                      setCondicionesPago({
                        ...condicionesPago,
                        dias_credito: dias,
                        fecha_vencimiento: calcularFechaVencimiento(dias)
                      });
                    }}
                    inputProps={{ min: 1 }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Fecha de Vencimiento"
                    type="date"
                    value={condicionesPago.fecha_vencimiento}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ readOnly: true }}
                    helperText="Calculada automáticamente"
                  />
                </Grid>
              </>
            )}
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nota / Observación de la compra"
                multiline
                rows={2}
                value={condicionesPago.observaciones_pago}
                onChange={(e) => setCondicionesPago({
                  ...condicionesPago,
                  observaciones_pago: e.target.value
                })}
                placeholder="Ej: Compra de suministros para oficina, Reposición de inventario..."
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Cuentas Contables */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom color="primary">
            📊 Cuentas Contables Principales
          </Typography>
          
          <Grid container spacing={2}>
            {/* IVA Compras - Solo lectura */}
            {(parseFloat(factura.totales.iva15 || factura.totales.totalIVA || 0) > 0) && (
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="IVA Pagado (Crédito Tributario)"
                  value={(() => {
                    const c = cuentas.find(x => x.id === cuentasSeleccionadas.ivaPagado);
                    return c ? `${c.codigo} - ${c.nombre}` : "No configurado";
                  })()}
                  InputProps={{ readOnly: true }}
                  variant="filled"
                  size="small"
                  helperText={`IVA 15%: $${parseFloat(factura.totales.iva15 || factura.totales.totalIVA || 0).toFixed(2)}`}
                />
              </Grid>
            )}

            {/* CRÉDITO: Proveedores - Solo lectura */}
            {condicionesPago.forma_pago !== "CONTADO" && (
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Proveedores (Cuenta por Pagar)"
                  value={(() => {
                    const c = cuentas.find(x => x.id === cuentasSeleccionadas.cuentaPago);
                    return c ? `${c.codigo} - ${c.nombre}` : "No configurado";
                  })()}
                  InputProps={{ readOnly: true }}
                  variant="filled"
                  size="small"
                  helperText={`Deuda con proveedor: $${factura.totales.importeTotal}`}
                />
              </Grid>
            )}

            {/* CONTADO: Caja/Bancos - EDITABLE */}
            {condicionesPago.forma_pago === "CONTADO" && (
              <Grid item xs={12} md={4}>
                <FormControl fullWidth required>
                  <InputLabel>Pago desde (Caja/Bancos) *</InputLabel>
                  <Select
                    value={cuentasSeleccionadas.cuentaPago || ""}
                    label="Pago desde (Caja/Bancos) *"
                    onChange={(e) => setCuentasSeleccionadas({
                      ...cuentasSeleccionadas,
                      cuentaPago: e.target.value
                    })}
                    size="small"
                  >
                    {cuentas.filter(c => c.acepta_movimiento && (
                      c.codigo?.startsWith("10101") || 
                      c.nombre?.toLowerCase().includes("caja") || 
                      c.nombre?.toLowerCase().includes("banco")
                    )).map(c => (
                      <MenuItem key={c.id} value={c.id}>{c.codigo} - {c.nombre}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                  De dónde sale el dinero (${factura.totales.importeTotal})
                </Typography>
              </Grid>
            )}

            {/* Tipo de Gasto/Destino - EDITABLE */}
            <Grid item xs={12} md={condicionesPago.forma_pago === "CONTADO" && parseFloat(factura.totales.iva15 || factura.totales.totalIVA || 0) > 0 ? 4 : 4}>
              <Autocomplete
                options={cuentas.filter(c => c.acepta_movimiento && (
                  c.codigo?.startsWith("10103") || c.codigo?.startsWith("10201") ||
                  c.codigo?.startsWith("501") || c.codigo?.startsWith("502") ||
                  c.codigo?.startsWith("503") || c.codigo?.startsWith("504") || c.codigo?.startsWith("505")
                ))}
                getOptionLabel={(option) => `${option.codigo} - ${option.nombre}`}
                groupBy={(option) => {
                  if (option.codigo?.startsWith("10103")) return "Inventario";
                  if (option.codigo?.startsWith("10201")) return "Propiedad, Planta y Equipo";
                  if (option.codigo?.startsWith("501")) return "Costo de Ventas";
                  if (option.codigo?.startsWith("502")) return "Gastos Administrativos";
                  if (option.codigo?.startsWith("503")) return "Gastos de Ventas";
                  return "Otros Gastos";
                }}
                value={cuentas.find(c => c.id === cuentasSeleccionadas.cuentaDestino) || null}
                onChange={(_, newValue) => setCuentasSeleccionadas({
                  ...cuentasSeleccionadas,
                  cuentaDestino: newValue?.id || ""
                })}
                renderInput={(params) => (
                  <TextField {...params} label="Destino de la Compra *" size="small"
                    placeholder="Buscar por código o nombre..." />
                )}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                noOptionsText="No se encontró la cuenta"
                size="small"
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                Inventario, Gastos, Activo Fijo, etc.
              </Typography>
            </Grid>
          </Grid>

        </CardContent>
      </Card>

      {/* Resumen */}
      <Card sx={{ bgcolor: "#e8f5e9" }}>
        <CardContent>
          <Typography variant="h6" gutterBottom fontWeight={700}>
            📋 Resumen
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography>Subtotal 0%:</Typography>
            </Grid>
            <Grid item xs={6} sx={{ textAlign: "right" }}>
              <Typography fontWeight={600}>${factura.totales.subtotal0 || 0}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography>Subtotal 15%:</Typography>
            </Grid>
            <Grid item xs={6} sx={{ textAlign: "right" }}>
              <Typography fontWeight={600}>${factura.totales.subtotal15 || 0}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography>IVA 15%:</Typography>
            </Grid>
            <Grid item xs={6} sx={{ textAlign: "right" }}>
              <Typography fontWeight={600}>${factura.totales.iva15 || factura.totales.totalIVA || 0}</Typography>
            </Grid>
            <Grid item xs={12}><Divider /></Grid>
            <Grid item xs={6}>
              <Typography variant="h6" color="primary" fontWeight={700}>TOTAL:</Typography>
            </Grid>
            <Grid item xs={6} sx={{ textAlign: "right" }}>
              <Typography variant="h6" color="primary" fontWeight={700}>
                ${factura.totales.importeTotal}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Botones */}
      <Box sx={{ mt: 4, display: "flex", justifyContent: "space-between" }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={onCancelar}
          disabled={loading}
        >
          Atrás
        </Button>
        <Button
          variant="contained"
          size="large"
          startIcon={<CheckCircle />}
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? "Registrando..." : "Confirmar Compra"}
        </Button>
      </Box>
    </Box>
  );
}
